import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, CalendarCheck, BadgeCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { PROGRAMS } from "../mock";
import Seo from "../components/Seo";

const Programs = () => {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver((es) => es.forEach((e) => e.isIntersecting && e.target.classList.add("is-visible")), { threshold: 0.12 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const jump = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 90, behavior: "smooth" });
  };

  return (
    <div className="pt-24">
      <Seo
        title="BJJ Programs in Alameda — Toddlers, Kids, Teens & Adults | Alameda BJJ"
        description="Brazilian Jiu-Jitsu programs for every age in Alameda, CA: Toddlers, Kids, Teens, Adults, Self-Defense & Verbal Jiu-Jitsu. Beginners welcome — your first class is free."
        path="/programs"
        breadcrumb={[{ name: "Home", path: "/" }, { name: "Programs", path: "/programs" }]}
      />
      <section className="bg-[#F4F5F1] py-16">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <span className="text-sm font-semibold tracking-[0.2em] text-br-green uppercase">Programs</span>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold text-[#1A1A1A] max-w-3xl leading-tight">
            Find the Right Class for You
          </h1>
          <p className="mt-5 text-neutral-600 max-w-2xl leading-relaxed">
            Every program is built on authentic Brazilian Jiu-Jitsu and taught in a safe, motivating environment. No experience needed — and your first class is always free.
          </p>
          {/* Quick-jump pills */}
          <div className="mt-8 flex flex-wrap gap-2.5">
            {PROGRAMS.map((p) => (
              <a
                key={p.id}
                href={`#${p.id}`}
                onClick={jump(p.id)}
                className="inline-flex rounded-full bg-white border border-neutral-200 px-4 py-2 text-sm font-medium text-neutral-700 hover:border-br-green hover:text-br-green transition-colors"
              >
                {p.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 space-y-20">
          {PROGRAMS.map((p, idx) => (
            <div key={p.id} id={p.id} className="reveal grid lg:grid-cols-2 gap-12 items-center scroll-mt-28">
              <div className={idx % 2 ? "lg:order-2" : ""}>
                <div className="relative">
                  <img src={p.image} alt={`${p.title} at Alameda Brazilian Jiu-Jitsu`} className="rounded-2xl w-full h-[400px] object-cover shadow-xl" />
                  <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full bg-white/95 backdrop-blur px-3.5 py-1.5 text-xs font-bold text-br-green shadow-sm">
                    <BadgeCheck size={14} /> Free Trial Class
                  </span>
                </div>
              </div>
              <div className={idx % 2 ? "lg:order-1" : ""}>
                <span className="text-sm font-semibold text-br-green tracking-wide">{p.ages}</span>
                <h2 className="mt-2 font-display text-3xl font-bold text-[#1A1A1A]">{p.title}</h2>
                <p className="mt-4 text-neutral-600 leading-relaxed">{p.short}</p>
                <ul className="mt-6 space-y-3">
                  {p.points.map((pt, i) => (
                    <li key={i} className="flex items-start gap-3 text-neutral-700">
                      <span className="h-6 w-6 rounded-full bg-br-green/10 flex items-center justify-center shrink-0 mt-0.5">
                        <Check size={14} className="text-br-green" />
                      </span>
                      {pt}
                    </li>
                  ))}
                </ul>
                <div className="mt-7 flex items-center gap-2 text-sm text-neutral-500">
                  <span className="font-semibold text-[#1A1A1A]">Memberships from $155/mo</span>
                  <span className="text-neutral-300">·</span>
                  <span>First class free</span>
                </div>
                <div className="mt-5 flex flex-wrap gap-3">
                  <Link to="/trial">
                    <Button className="bg-br-green hover:bg-[#00822f] text-white rounded-full px-7 h-11 shadow-sm shadow-br-green/30">
                      <CalendarCheck size={17} className="mr-1.5" /> Start Free Trial
                    </Button>
                  </Link>
                  <Link to="/pricing"><Button variant="outline" className="rounded-full px-6 h-11 border-neutral-300">See Pricing</Button></Link>
                  <Link to="/schedule"><Button variant="outline" className="rounded-full px-6 h-11 border-neutral-300">Schedule <ArrowRight size={15} className="ml-1" /></Button></Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="pb-20">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="reveal rounded-3xl bg-br-green px-8 py-14 sm:px-14 text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 h-40 w-40 bg-br-yellow/20 rounded-full blur-3xl" />
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-white relative">Not Sure Which Class Fits?</h2>
            <p className="mt-4 text-green-50 max-w-xl mx-auto relative">Book a free trial and we'll help you find the perfect program for your age and goals.</p>
            <div className="mt-8 relative">
              <Link to="/trial"><Button className="h-12 px-8 bg-white text-br-green hover:bg-neutral-100 rounded-full font-semibold">Book a Free Trial</Button></Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Programs;
